import React from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  People as PeopleIcon,
  Place as PlaceIcon,
  Favorite as FavoriteIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { useQuery } from "@tanstack/react-query";
import { useDashboardStats } from "../hooks/useDashboardStats";
import apiClient from "../lib/api";

const AdminDashboard: React.FC = () => {
  const { data, isLoading, error, refetch } = useDashboardStats();

  // Fetch favorites count array for the chart (backend returns { favoriteCounts: [...] })
  const { data: favoritesCountArrayData } = useQuery({
    queryKey: ["dashboard", "favoritesCountArray"],
    queryFn: async () => {
      // Call API directly to get raw response (backend returns { favoriteCounts: [...] })
      const response = await apiClient.get("/api/admin/reports/favoritescount");
      const data = response.data.data || response.data;
      // Backend returns { favoriteCounts: [...] }
      return data?.favoriteCounts || [];
    },
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading dashboard data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading dashboard data. Please try again.
        </Alert>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Unable to load dashboard statistics.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  // Transform data for charts
  const placesPerCountryData = data.placesPerCountry?.map((item, index) => ({
    id: index,
    value: item.count,
    label: item.country || "Unknown",
  })) || [];

  const placesPerStatusData = data.placesPerStatus?.map((item, index) => ({
    id: index,
    value: item.count,
    label: item.status.charAt(0).toUpperCase() + item.status.slice(1),
  })) || [];

  // Sort and transform users created by date
  const sortedUsersByDate = [...(data.usersCreatedByDate || [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const userGrowthDates = sortedUsersByDate.map((item) => item.date);
  const userGrowthCounts = sortedUsersByDate.map((item) => item.count);

  // Calculate total favorites count from array or use the number
  const favoritesCountValue =
    data.favoritesCount ??
    (favoritesCountArrayData && Array.isArray(favoritesCountArrayData)
      ? favoritesCountArrayData.reduce((sum, item) => sum + (item.count || 0), 0)
      : 0);

  // Transform favorites count array for chart (top 10 places)
  const topFavoritesData =
    favoritesCountArrayData && Array.isArray(favoritesCountArrayData)
      ? favoritesCountArrayData
          .slice(0, 10)
          .map((item: any) => ({
            placeName: item.placeName || "Unknown",
            count: item.count || 0,
          }))
      : [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Overview of platform statistics and analytics
      </Typography>

      {/* Summary Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Users</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {data.usersTotal ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PlaceIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Places</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {data.placesTotal ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <FavoriteIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Favorites</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {favoritesCountValue}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <StarIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Places with Ratings</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {data.placesWithRating ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Places Per Country and Places Per Status Pie Charts */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Places by Country
            </Typography>
            {placesPerCountryData.length > 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                <PieChart
                  series={[{ data: placesPerCountryData }]}
                  height={400}
                  margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Places by Status
            </Typography>
            {placesPerStatusData.length > 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                <PieChart
                  series={[{ data: placesPerStatusData }]}
                  height={400}
                  margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Top Favorite Places Bar Chart */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Favorite Places
            </Typography>
            {topFavoritesData.length > 0 ? (
              <BarChart
                series={[{ data: topFavoritesData.map((item) => item.count) }]}
                height={400}
                xAxis={[
                  {
                    data: topFavoritesData.map((item) => item.placeName),
                    scaleType: "band",
                  },
                ]}
                margin={{ top: 20, bottom: 60, left: 40, right: 10 }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* User Growth Over Time Line Chart */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              User Growth Over Time
            </Typography>
            {userGrowthDates.length > 0 ? (
              <LineChart
                xAxis={[
                  {
                    data: userGrowthDates,
                    scaleType: "point",
                  },
                ]}
                series={[
                  {
                    data: userGrowthCounts,
                    label: "New Users",
                  },
                ]}
                height={400}
                margin={{ top: 20, bottom: 60, left: 40, right: 10 }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

export default AdminDashboard;
